/**
 * US Regions configuration with colors and state mappings
 * Based on 5-region division: West, Midwest, Northeast, Southwest, Southeast
 */

export type TUSRegion = "West" | "Midwest" | "Northeast" | "Southwest" | "Southeast";

export interface IUSRegionConfig {
	name: TUSRegion;
	color: string;
	hoverColor: string;
	states: string[];
	labelCoordinates: [number, number];
}

// US Region colors - stylish, modern palette inspired by the reference image
export const US_REGION_COLORS: Record<TUSRegion, {fill: string; hover: string}> = {
	West: {fill: "#a3d8de", hover: "#1e4f56"}, // Teal/Turquoise
	Midwest: {fill: "#7bc3cb", hover: "#1e4f56"}, // Vibrant Green
	Northeast: {fill: "#38848e", hover: "#1e4f56"}, // Royal Blue
	Southwest: {fill: "#63abb4", hover: "#1e4f56"}, // Coral/Terracotta
	Southeast: {fill: "#33919e", hover: "#1e4f56"}, // Golden Olive
};

// State to region mapping with all US states (5 regions)
export const US_STATE_REGIONS: Record<string, TUSRegion> = {
	// West Region (Pacific Northwest + Mountain states)
	Washington: "West",
	Oregon: "West",
	California: "West",
	Nevada: "West",
	Idaho: "West",
	Montana: "West",
	Wyoming: "West",
	Utah: "West",
	Colorado: "West",
	Alaska: "West",
	Hawaii: "West",

	// Midwest Region (Great Plains + Great Lakes)
	"North Dakota": "Midwest",
	"South Dakota": "Midwest",
	Nebraska: "Midwest",
	Kansas: "Midwest",
	Minnesota: "Midwest",
	Iowa: "Midwest",
	Missouri: "Midwest",
	Wisconsin: "Midwest",
	Illinois: "Midwest",
	Michigan: "Midwest",
	Indiana: "Midwest",
	Ohio: "Midwest",

	// Northeast Region
	Maine: "Northeast",
	"New Hampshire": "Northeast",
	Vermont: "Northeast",
	Massachusetts: "Northeast",
	"Rhode Island": "Northeast",
	Connecticut: "Northeast",
	"New York": "Northeast",
	"New Jersey": "Northeast",
	Pennsylvania: "Northeast",

	// Southwest Region
	Arizona: "Southwest",
	"New Mexico": "Southwest",
	Texas: "Southwest",
	Oklahoma: "Southwest",

	// Southeast Region
	Arkansas: "Southeast",
	Louisiana: "Southeast",
	Mississippi: "Southeast",
	Alabama: "Southeast",
	Tennessee: "Southeast",
	Kentucky: "Southeast",
	"West Virginia": "Southeast",
	Virginia: "Southeast",
	"North Carolina": "Southeast",
	"South Carolina": "Southeast",
	Georgia: "Southeast",
	Florida: "Southeast",
	Maryland: "Southeast",
	Delaware: "Southeast",
	"District of Columbia": "Southeast",
};

// Region label positions on the map
export const US_REGION_LABELS: IUSRegionConfig[] = [
	{
		name: "West",
		color: US_REGION_COLORS.West.fill,
		hoverColor: US_REGION_COLORS.West.hover,
		states: Object.entries(US_STATE_REGIONS)
			.filter(([, region]) => region === "West")
			.map(([state]) => state),
		labelCoordinates: [-120, 42],
	},
	{
		name: "Midwest",
		color: US_REGION_COLORS.Midwest.fill,
		hoverColor: US_REGION_COLORS.Midwest.hover,
		states: Object.entries(US_STATE_REGIONS)
			.filter(([, region]) => region === "Midwest")
			.map(([state]) => state),
		labelCoordinates: [-95, 43],
	},
	{
		name: "Northeast",
		color: US_REGION_COLORS.Northeast.fill,
		hoverColor: US_REGION_COLORS.Northeast.hover,
		states: Object.entries(US_STATE_REGIONS)
			.filter(([, region]) => region === "Northeast")
			.map(([state]) => state),
		labelCoordinates: [-74, 43],
	},
	{
		name: "Southwest",
		color: US_REGION_COLORS.Southwest.fill,
		hoverColor: US_REGION_COLORS.Southwest.hover,
		states: Object.entries(US_STATE_REGIONS)
			.filter(([, region]) => region === "Southwest")
			.map(([state]) => state),
		labelCoordinates: [-107, 33],
	},
	{
		name: "Southeast",
		color: US_REGION_COLORS.Southeast.fill,
		hoverColor: US_REGION_COLORS.Southeast.hover,
		states: Object.entries(US_STATE_REGIONS)
			.filter(([, region]) => region === "Southeast")
			.map(([state]) => state),
		labelCoordinates: [-86, 33],
	},
];

/**
 * Get the region for a given US state name
 */
export const getUSStateRegion = (stateName: string): TUSRegion | null => {
	return US_STATE_REGIONS[stateName] || null;
};

/**
 * Get the color for a US state based on its region
 */
export const getUSStateColor = (stateName: string): string => {
	const region = getUSStateRegion(stateName);
	if (region) {
		return US_REGION_COLORS[region].fill;
	}

	return "#DBE4E6"; // Default gray color
};

/**
 * Get the hover color for a US state based on its region
 */
export const getUSStateHoverColor = (stateName: string): string => {
	const region = getUSStateRegion(stateName);
	if (region) {
		return US_REGION_COLORS[region].hover;
	}

	return "#C5D0D2"; // Default gray hover color
};

// Threshold zoom level to show US states detail
export const US_ZOOM_THRESHOLD = 2.5;

// US bounding box to detect if view is centered on US
export const US_BOUNDS = {
	west: -125,
	east: -67,
	north: 50,
	south: 24,
	center: [-98, 38] as [number, number],
};

/**
 * Check if the current view is focused on the US
 */
export const isViewingUS = (center: [number, number], zoom: number): boolean => {
	const [lng, lat] = center;

	return (
		zoom >= US_ZOOM_THRESHOLD &&
		lng >= US_BOUNDS.west &&
		lng <= US_BOUNDS.east &&
		lat >= US_BOUNDS.south &&
		lat <= US_BOUNDS.north
	);
};
